/**
 * Deep-link preservation across the Keycloak login round-trip.
 *
 * The OmniWound web client registers only the bare origin as its Keycloak
 * redirect URI — an intentional security invariant (wildcards are rejected by
 * `scripts/ci/check-omniwound-auth-config.js`). With `onLoad: 'login-required'`
 * the access token lives only in memory, so every hard reload has no token and
 * round-trips through Keycloak, which returns the browser to that bare origin.
 * The path + query the user reloaded on (e.g. `/visits?documenting=<id>`, the
 * open progress-note builder) is therefore lost, and `/` redirects to the demo
 * home (`/patients`).
 *
 * To survive the round-trip we snapshot the current relative URL into
 * sessionStorage BEFORE the redirect fires (sessionStorage persists across a
 * same-tab navigation to another origin and back) and restore it from the `/`
 * route once Keycloak has re-authenticated. See [[app-provider]] /
 * [[routes/index]].
 */
const STORAGE_KEY = 'omniwound.deep-link';

// Guards against restoring a snapshot that was never consumed on the expected
// `/` landing (e.g. a reload that did not actually round-trip): an entry older
// than this is treated as stale and ignored.
const MAX_AGE_MS = 60_000;

type SavedDeepLink = {
  href: string;
  savedAt: number;
};

function storage(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.sessionStorage;
}

/**
 * True when the current query string carries Keycloak's OAuth response params.
 * The login redirect lands on the origin root with these appended; that URL
 * must never be snapshotted as the deep link to restore.
 */
function isAuthCallback(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    (params.has('code') && params.has('state')) ||
    params.has('error') ||
    params.has('session_state')
  );
}

/** A restorable deep link is same-origin and relative — never protocol-relative. */
function isSafeRelativeHref(href: string): boolean {
  const containsControlCharacter = [...href].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 0x1f || code === 0x7f;
  });
  if (
    !href.startsWith('/') ||
    href.startsWith('//') ||
    href.includes('\\') ||
    containsControlCharacter
  ) {
    return false;
  }

  try {
    return (
      new URL(href, window.location.origin).origin === window.location.origin
    );
  } catch {
    return false;
  }
}

/**
 * Snapshots the current relative URL so it can be restored after the Keycloak
 * login round-trip. No-op for the origin root, the login page, auth callbacks,
 * or when sessionStorage is unavailable.
 */
export function captureDeepLink(): void {
  const store = storage();
  if (!store) {
    return;
  }

  const { pathname, search } = window.location;
  if (pathname === '/' || pathname === '/login' || isAuthCallback(search)) {
    return;
  }

  try {
    const payload: SavedDeepLink = {
      // Fragments can contain sensitive client-only state and never need to
      // cross the identity-provider round-trip. Route + query are preserved.
      href: `${pathname}${search}`,
      savedAt: Date.now(),
    };
    store.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / quota errors are non-fatal: the reload simply won't
    // restore the deep link, which degrades to the pre-existing behaviour.
  }
}

/**
 * Reads and clears the snapshotted deep link. Returns the relative href to
 * restore, or null when nothing valid is stored (missing, stale, malformed, or
 * not a safe same-origin relative path).
 */
export function consumeDeepLink(): string | null {
  const store = storage();
  if (!store) {
    return null;
  }

  try {
    const raw = store.getItem(STORAGE_KEY);
    // Consume once: a restore attempt must not repeat on a later `/` visit.
    store.removeItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<SavedDeepLink> | null;
    if (
      !parsed ||
      typeof parsed.href !== 'string' ||
      typeof parsed.savedAt !== 'number' ||
      !isSafeRelativeHref(parsed.href) ||
      Date.now() - parsed.savedAt > MAX_AGE_MS
    ) {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}
